import { AfterViewInit, Component, DestroyRef, effect, inject, NgZone, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import { AuthService } from '@app/core/auth/auth.service';
import { HelpWidgetService } from '@app/core/services/help-widget.service';
import { FaqComponent } from '@app/modules/faq/faq.component';

@Component({
    selector: 'app-help-widget',
    standalone: true,
    imports: [DialogModule, FaqComponent],
    templateUrl: './help-widget.component.html',
    styleUrl: './help-widget.component.scss'
})
export class HelpWidgetComponent implements AfterViewInit {
    readonly widget = inject(HelpWidgetService);
    private readonly auth = inject(AuthService);
    private readonly router = inject(Router);
    private readonly zone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);
    readonly user = toSignal(this.auth.currentUser$, { initialValue: this.auth.currentUserSnapshot });
    readonly url = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => event.urlAfterRedirects)
        ),
        { initialValue: this.router.url }
    );
    readonly obstructed = signal(false);
    readonly dialogVisible = signal(false);
    readonly dialogClosing = signal(false);

    constructor() {
        effect(() => {
            if (!this.available) this.widget.opened.set(false);
        });
        effect(() => {
            const visible = this.available && this.widget.opened();
            // PrimeNG must finish removing the outgoing dialog before creating another.
            if (this.dialogClosing()) return;
            if (this.dialogVisible() && !visible) this.dialogClosing.set(true);
            this.dialogVisible.set(visible);
        });
        this.auth.currentUser$
            .pipe(
                map((user) => user?.id),
                distinctUntilChanged(),
                takeUntilDestroyed()
            )
            .subscribe(() => {
                this.widget.hidden.set(false);
                this.widget.opened.set(false);
            });
    }

    get available(): boolean {
        return !!this.user() && !/^\/faq(?:[;/?#]|$)/.test(this.url()) && (this.url().startsWith('/auth/access') || !/^\/(?:login|auth|reset-password|landing)(?:[/?#]|$)/.test(this.url()));
    }

    ngAfterViewInit(): void {
        this.zone.runOutsideAngular(() => {
            let frame = 0;
            const schedule = () => {
                if (frame) return;
                frame = requestAnimationFrame(() => {
                    frame = 0;
                    this.updateLauncherVisibility();
                });
            };
            const observer = new MutationObserver(schedule);
            observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'open', 'aria-hidden'] });
            window.addEventListener('resize', schedule);
            schedule();
            this.destroyRef.onDestroy(() => {
                observer.disconnect();
                cancelAnimationFrame(frame);
                window.removeEventListener('resize', schedule);
            });
        });
    }

    updateLauncherVisibility(): void {
        if (!this.available || this.widget.hidden() || this.widget.opened()) return;
        const modalOpen = Array.from(document.querySelectorAll<HTMLElement>('[aria-modal="true"]')).some((element) => element.getBoundingClientRect().width > 0);
        this.zone.run(() => this.obstructed.set(modalOpen));
    }
}
